// ---------------------------------------------------------------------------
// PWA_ControlBillet — Scan history screen
// Filterable list of today's scans with refresh capability.
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback } from 'react';
import { HistoryItem } from '@/components/HistoryItem';
import type { ScanEntry, HistoryEntry } from '@/lib/types';

type Filter = 'all' | 'valid' | 'invalid';

// ---------------------------------------------------------------------------
// Refresh icon
// ---------------------------------------------------------------------------

function RefreshIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-accent-light"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HistoryScreen() {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(false);

  // Load scans from duplicateDetector (if available) or localStorage fallback.
  const loadScans = useCallback(async () => {
    setLoading(true);

    try {
      // Try loading from duplicateDetector service.
      const { duplicateDetector } = await import('@/crypto/duplicate-detector');
      const scans = await duplicateDetector.getTodayScans();
      setEntries(scans as HistoryEntry[]);
    } catch {
      // Fallback: read from localStorage scan history.
      try {
        const raw = localStorage.getItem('scan-history');
        if (raw) {
          const parsed = JSON.parse(raw) as HistoryEntry[];
          // Filter to today only.
          const today = new Date().toISOString().slice(0, 10);
          const todayEntries = parsed.filter(
            (e) => e.timestamp.slice(0, 10) === today,
          );
          setEntries(todayEntries);
        }
      } catch {
        // No data available.
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadScans();
  }, [loadScans]);

  // Filter entries.
  const filteredEntries = entries.filter((entry) => {
    if (filter === 'all') return true;
    if (filter === 'valid') return entry.status === 'valid';
    if (filter === 'invalid')
      return entry.status === 'invalid' || entry.status === 'duplicate';
    return true;
  });

  // Sort by timestamp descending (most recent first).
  const sortedEntries = [...filteredEntries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return (
    <div className="h-full flex flex-col pt-14">
      {/* Filter bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface-light border-b border-white/5">
        <div className="flex gap-2">
          <FilterButton
            label="Tous"
            active={filter === 'all'}
            count={entries.length}
            onClick={() => setFilter('all')}
          />
          <FilterButton
            label="Valides"
            active={filter === 'valid'}
            count={entries.filter((e) => e.status === 'valid').length}
            onClick={() => setFilter('valid')}
          />
          <FilterButton
            label="Invalides"
            active={filter === 'invalid'}
            count={
              entries.filter(
                (e) => e.status === 'invalid' || e.status === 'duplicate',
              ).length
            }
            onClick={() => setFilter('invalid')}
          />
        </div>

        {/* Refresh button */}
        <button
          type="button"
          onClick={loadScans}
          disabled={loading}
          className="w-9 h-9 rounded-full bg-surface-card flex items-center justify-center transition-all active:scale-90 disabled:opacity-50"
          aria-label="Rafraichir"
        >
          <RefreshIcon />
        </button>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 pb-20">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && sortedEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-600 mb-4"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <p className="text-gray-500 text-sm font-medium">
              Aucun scan aujourd&apos;hui
            </p>
            <p className="text-gray-600 text-xs mt-1">
              Les scans effectues apparaitront ici
            </p>
          </div>
        )}

        {!loading &&
          sortedEntries.map((entry) => (
            <HistoryItem key={entry.key} entry={entry} />
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter button sub-component
// ---------------------------------------------------------------------------

interface FilterButtonProps {
  label: string;
  active: boolean;
  count: number;
  onClick: () => void;
}

function FilterButton({ label, active, count, onClick }: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-150 min-h-[32px] ${
        active
          ? 'bg-accent text-white'
          : 'bg-surface-card text-gray-400 active:bg-white/10'
      }`}
    >
      {label}
      <span
        className={`ml-1 ${active ? 'text-white/80' : 'text-gray-500'}`}
      >
        ({count})
      </span>
    </button>
  );
}
